import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCompareVehiclesUrl } from '../../../lib/routes';
import { FALLBACK_IMAGE } from '../../../lib/imageUtils';

const RECENTLY_COMPARED_KEY = 'rockhound_recently_compared';

interface SavedVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  image: string;
  price: number;
}

interface RecentComparison {
  ids: string[];
  label: string;
  timestamp: number;
  vehicles: SavedVehicle[];
}

export default function RecentlyComparedStrip() {
  const [recent, setRecent] = useState<RecentComparison | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENTLY_COMPARED_KEY);
      if (!raw) return;
      const parsed: RecentComparison = JSON.parse(raw);
      // Only show if saved within the last 7 days and has 2+ vehicles
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      if (parsed.vehicles.length >= 2 && parsed.timestamp > sevenDaysAgo) {
        setRecent(parsed);
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  if (!recent || dismissed) return null;

  const compareUrl = getCompareVehiclesUrl(recent.ids);
  const timeAgo = (() => {
    const diff = Date.now() - recent.timestamp;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 2) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  })();

  return (
    <div className="rounded-2xl border border-[#E8E2D6] bg-[#FFFBF0] overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#E8E2D6]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#F5C842]/20 flex-shrink-0">
            <i className="ri-time-line text-[#D97706] text-sm" aria-hidden="true" />
          </div>
          <div>
            <p
              className="text-sm font-black text-[#1A2332] leading-tight"
              style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.04em' }}
            >
              RECENTLY COMPARED
            </p>
            <p className="text-[11px] text-[#9CA3AF] mt-0.5">
              Your last side-by-side · {timeAgo}
            </p>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#E8E2D6] transition-colors cursor-pointer flex-shrink-0"
          aria-label="Dismiss recently compared"
        >
          <i className="ri-close-line text-[#9CA3AF] text-base" aria-hidden="true" />
        </button>
      </div>

      {/* Vehicle thumbnails row */}
      <div className="px-5 py-4 flex items-center gap-4 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap sm:flex-nowrap">
          {recent.vehicles.map((v, idx) => (
            <div key={v.id} className="flex items-center gap-2 min-w-0">
              {idx > 0 && (
                <div className="hidden sm:flex w-6 h-6 flex-shrink-0 items-center justify-center">
                  <i className="ri-vs-line text-[#D0CBC0] text-xs" aria-hidden="true" />
                </div>
              )}
              <div className="flex items-center gap-2 bg-white rounded-xl border border-[#E8E2D6] px-3 py-2 min-w-0 flex-shrink-0">
                {/* Thumbnail */}
                <div
                  className="flex-shrink-0 rounded-lg overflow-hidden bg-[#F7F5F0]"
                  style={{ width: 44, height: 32 }}
                >
                  <img
                    src={imageErrors[v.id] ? FALLBACK_IMAGE : v.image || FALLBACK_IMAGE}
                    alt={`${v.year} ${v.make} ${v.model}`}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    onError={() =>
                      setImageErrors(prev => ({ ...prev, [v.id]: true }))
                    }
                  />
                </div>
                <div className="min-w-0">
                  <p
                    className="text-xs font-black text-[#1A2332] leading-tight truncate"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {v.year} {v.make}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF] truncate">{v.model}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          to={compareUrl}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-black text-sm text-[#1A2332] whitespace-nowrap flex-shrink-0 transition-opacity hover:opacity-80 cursor-pointer"
          style={{
            background: '#F5C842',
            fontFamily: "'Oswald', sans-serif",
            letterSpacing: '0.04em',
          }}
          aria-label="Jump back into your recent comparison"
        >
          <i className="ri-scales-3-line text-base" aria-hidden="true" />
          JUMP BACK IN
          <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

// Utility exported so compare page can save without importing the component
export function saveRecentComparison(data: RecentComparison): void {
  try {
    localStorage.setItem(RECENTLY_COMPARED_KEY, JSON.stringify(data));
  } catch {
    // localStorage might be full or unavailable — fail silently
  }
}
